import React, { useMemo, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import ToggleSwitch from '../../../Components/ToggleSwitch';

export interface FilterCheckboxOption {
    key: string;
    label: string;
}

interface FilterCheckboxColumnProps {
    title: string;
    options: FilterCheckboxOption[];
    isSelected: (key: string) => boolean;
    allSelected: boolean;
    onToggle: (key: string) => void;
    onToggleAll: () => void;
    /**
     * Placeholder for a search field above the rows. The search only narrows
     * the rows shown: "Alla" still acts on every option and hidden checked
     * options stay checked.
     */
    searchPlaceholder?: string;
}

interface FilterToggleRowProps {
    label: string;
    checked: boolean;
    onToggle: () => void;
}

/**
 * A menu row with a small toggle. The whole row is the click target: a mouse
 * click reaches it past the label (pointer-events: none) and a key press on the
 * toggle bubbles up to it, so the toggle's own onChange does nothing.
 */
export const FilterToggleRow: React.FC<FilterToggleRowProps> = ({ label, checked, onToggle }) => (
    <Dropdown.Item as="div" className="py-2" onClick={onToggle}>
        <label className="building-filter-toggle">
            <ToggleSwitch small checked={checked} onChange={() => undefined} />
            <span>{label}</span>
        </label>
    </Dropdown.Item>
);

/**
 * One column of the building filter dropdown: a header, an optional search
 * field, an "Alla" toggle and a toggle row per option.
 */
const FilterCheckboxColumn: React.FC<FilterCheckboxColumnProps> = ({
    title,
    options,
    isSelected,
    allSelected,
    onToggle,
    onToggleAll,
    searchPlaceholder
}) => {
    const [search, setSearch] = useState('');
    const normalizedSearch = search.trim().toLowerCase();
    const visibleOptions = useMemo(
        () => normalizedSearch
            ? options.filter(option => option.label.toLowerCase().includes(normalizedSearch))
            : options,
        [options, normalizedSearch]
    );

    return (
        <div className="building-filter-column">
            <Dropdown.Header>{title}</Dropdown.Header>
            {searchPlaceholder && (
                <div className="building-filter-column__search">
                    <Form.Control
                        type="search"
                        size="sm"
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            )}
            <FilterToggleRow label="Alla" checked={allSelected} onToggle={onToggleAll} />
            <Dropdown.Divider />
            {visibleOptions.map(option => (
                <FilterToggleRow
                    key={option.key}
                    label={option.label}
                    checked={isSelected(option.key)}
                    onToggle={() => onToggle(option.key)}
                />
            ))}
            {normalizedSearch && visibleOptions.length === 0 && (
                <div className="building-filter-column__hint">Inga träffar</div>
            )}
        </div>
    );
};

export default FilterCheckboxColumn;
